// Export d'un decompte de semaines en Markdown : plan lisible par une
// personne comme par une IA (contexte, legende, synthese, detail par semaine).
import type { ZoneRow } from '../Construction/allureTable.ts';
import { formaterDuree, formaterDureeCourse } from '../Construction/formatDuree.ts';
import { normaliserAllures } from '../Construction/seanceEngine.ts';
import { ajouterJours, formaterKm, iso } from './dates.ts';
import { dateLocale, semainesEntre } from './decompte.ts';
import { infoSemaine, listerPlanning, listerSemaines, type Cible, type Decompte, type SeancePlanifiee, type Semaine } from './planningApi.ts';

// Periode lue par appel (le serveur limite a 62 jours).
const SEMAINES_PAR_APPEL = 8;

export type ContextePlan = {
  athlete: Cible;
  nomAthlete: string;
  vma: number;
  table: ZoneRow[];
  // Notation affichee (recuperations personnalisees appliquees).
  ajuster: (notation: string) => string;
  // Type de seance (Long, Seuil...) ou null.
  libelleType: (p: SeancePlanifiee) => string | null | undefined;
};

/** Lit les seances et types de semaine du decompte, par tranches. */
export async function lirePeriode(d: Decompte, athlete: Cible): Promise<{ seances: SeancePlanifiee[]; types: Map<string, string> }> {
  const seances: SeancePlanifiee[] = [];
  const types = new Map<string, string>();
  const fin = iso(ajouterJours(dateLocale(d.lundiCible), 6));
  for (let debut = dateLocale(d.lundiDebut); iso(debut) <= fin; debut = ajouterJours(debut, 7 * SEMAINES_PAR_APPEL)) {
    const finTranche = iso(ajouterJours(debut, 7 * SEMAINES_PAR_APPEL - 1));
    const borne = finTranche < fin ? finTranche : fin;
    const [liste, semaines] = await Promise.all([listerPlanning(iso(debut), borne, athlete), listerSemaines(iso(debut), borne, athlete)]);
    seances.push(...liste);
    semaines.forEach((s: Semaine) => s.type && types.set(s.lundi, s.type));
  }
  return { seances, types };
}

const jourLong = (d: Date) => d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const dateCourte = (d: Date) => d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

function allure(kmh: number): string {
  const s = Math.round(3600 / kmh);
  return `${Math.floor(s / 60)}'${String(s % 60).padStart(2, '0')}''/km`;
}

function libelleCategorie(c?: string | null): string {
  switch (c) {
    case 'COMPETITION': return 'Compétition';
    case 'REPOS': return 'Repos';
    case 'NOTE': return 'Note';
    default: return 'Séance';
  }
}

const duree = (p: SeancePlanifiee) => (p.dureeTotaleS ? (p.categorie === 'COMPETITION' ? formaterDureeCourse(p.dureeTotaleS) : formaterDuree(p.dureeTotaleS)) : '');

/** Markdown du plan d'un decompte. */
export async function genererPlan(d: Decompte, c: ContextePlan): Promise<string> {
  const { seances, types } = await lirePeriode(d, c.athlete);
  const nb = semainesEntre(d.lundiDebut, d.lundiCible) + 1;
  const debut = dateLocale(d.lundiDebut);
  const fin = ajouterJours(dateLocale(d.lundiCible), 6);
  const parJour = new Map<string, SeancePlanifiee[]>();
  seances.forEach((p) => parJour.set(p.date, [...(parJour.get(p.date) ?? []), p]));
  parJour.forEach((l) => l.sort((a, b) => a.ordre - b.ordre));
  const competitions = seances.filter((p) => p.categorie === 'COMPETITION').sort((a, b) => a.date.localeCompare(b.date));
  const objectif = competitions.filter((p) => p.date >= d.lundiCible);

  const l: string[] = [];
  l.push(`# Plan d'entraînement : S${nb} à S1`, '');
  l.push(`- Athlète : ${c.nomAthlete}`);
  l.push(`- Objectif (S1) : semaine du ${dateCourte(dateLocale(d.lundiCible))}${objectif.length ? `, ${objectif.map((p) => `${normaliserAllures(p.nom)} le ${jourLong(dateLocale(p.date))}`).join(' ; ')}` : ''}`);
  l.push(`- Période : du ${jourLong(debut)} au ${jourLong(fin)} (${nb} semaine${nb > 1 ? 's' : ''})`);
  if (c.vma) l.push(`- VMA : ${c.vma.toLocaleString('fr-FR')} km/h`);
  l.push(`- Exporté le : ${dateCourte(new Date())}`, '');

  l.push('## Comment lire ce plan', '');
  l.push(`- Les semaines sont décomptées jusqu'à l'objectif : S${nb} est la première, S1 la semaine de l'objectif.`);
  l.push("- Type de semaine : Normale ; Assimilation (semaine allégée pour assimiler la charge des semaines précédentes) ; Affûtage (semaine précédant une compétition).");
  l.push("- Notation des séances : `durée ou distance@allure`. `'` = minutes, `''` = secondes, `m`/`km` = distance. `-R2'` = récupération de 2 min après la fraction. `+` enchaîne les blocs, `Nx(...)` répète un bloc N fois. Exemple : `3x(5km@MP-R3'30'')` = 3 fois 5 km à allure marathon, 3 min 30 de récupération.");
  l.push("- Séance progressive : deux codes d'allure séparés par `-` après `@`. Exemple : `30'@HP-10K` = 30 min en progressant de l'allure semi à l'allure 10 km ; durée, distance et charge sont calculées à la moyenne des deux allures. Un `-` suivi d'une durée ou d'un `R` reste une récupération (`5km@MP-R3'`).");
  l.push('- Les allures sont désignées par les codes du tableau ci-dessous. Charge en UA (unités arbitraires) ; RPE = effort perçu sur 10.', '');

  if (c.table.length && c.vma) {
    l.push('## Allures', '', '| Code | Zone | % VMA | Vitesse | Allure |', '|---|---|---|---|---|');
    c.table.forEach((z) => {
      const v = c.vma * z.pctVMA;
      l.push(`| ${z.code} | ${z.nom} | ${Math.round(z.pctVMA * 1000) / 10} % | ${v.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} km/h | ${allure(v)} |`);
    });
    l.push('');
  }

  // Synthese puis detail, semaine par semaine.
  const semaines = Array.from({ length: nb }, (_, i) => ajouterJours(debut, 7 * i));
  const resume = semaines.map((lundi) => {
    const jours = Array.from({ length: 7 }, (_, j) => iso(ajouterJours(lundi, j)));
    const liste = jours.flatMap((j) => parJour.get(j) ?? []);
    const actives = liste.filter((p) => p.categorie !== 'REPOS' && p.categorie !== 'NOTE');
    return {
      lundi,
      jours,
      n: nb - semaines.indexOf(lundi),
      type: infoSemaine(types.get(iso(lundi)))?.libelle ?? 'Normale',
      nbSeances: actives.length,
      duree: actives.reduce((s, p) => s + (p.dureeTotaleS ?? 0), 0),
      distance: actives.reduce((s, p) => s + (p.distanceTotaleM ?? 0), 0),
      charge: actives.reduce((s, p) => s + (p.chargeUA ?? 0), 0),
    };
  });
  l.push('## Synthèse', '', '| Semaine | Lundi | Type | Séances | Durée | Distance | Charge |', '|---|---|---|---|---|---|---|');
  resume.forEach((r) => l.push(`| S${r.n} | ${iso(r.lundi)} | ${r.type} | ${r.nbSeances} | ${r.duree ? formaterDuree(r.duree) : '-'} | ${formaterKm(r.distance) || '-'} | ${r.charge ? Math.round(r.charge) + ' UA' : '-'} |`));
  l.push('');

  resume.forEach((r) => {
    l.push(`## S${r.n} : semaine du ${dateCourte(r.lundi)} (${r.type})`, '');
    let vide = true;
    r.jours.forEach((j) => (parJour.get(j) ?? []).forEach((p) => {
      vide = false;
      const type = c.libelleType(p);
      l.push(`### ${jourLong(dateLocale(j))} : ${normaliserAllures(p.nom) || libelleCategorie(p.categorie)}`, '');
      l.push(`- Catégorie : ${libelleCategorie(p.categorie)}${type ? ` (${type})` : ''}`);
      if (p.categorie !== 'REPOS' && p.categorie !== 'NOTE') {
        if (p.nomModele) {
          l.push(`- Modèle : ${p.nomModele}`);
          if (p.notationEchauffement) l.push(`- Échauffement : \`${c.ajuster(p.notationEchauffement)}\``);
          l.push(`- Corps de séance : \`${c.ajuster(p.notationCorps ?? p.notation)}\``);
          if (p.notationRecuperation) l.push(`- Retour au calme : \`${c.ajuster(p.notationRecuperation)}\``);
        } else if (p.notation) {
          l.push(`- Séance : \`${c.ajuster(p.notation)}\``);
        }
        const mesures = [duree(p) && `durée ${duree(p)}`, formaterKm(p.distanceTotaleM) && `distance ${formaterKm(p.distanceTotaleM)}`,
          p.chargeUA ? `charge ${Math.round(p.chargeUA)} UA` : '', p.rpe ? `RPE ${p.rpe}` : '', p.zonePrincipale ? `zone principale ${p.zonePrincipale}` : '']
          .filter(Boolean);
        if (mesures.length) l.push(`- Mesures : ${mesures.join(', ')}`);
      }
      if (p.notes) l.push(`- Notes : ${p.notes.replace(/\s*\n\s*/g, ' / ')}`);
      l.push('');
    }));
    if (vide) l.push('Aucune séance planifiée.', '');
  });
  return l.join('\n');
}

/** Telecharge le plan au format Markdown. */
export function telechargerPlan(markdown: string, d: Decompte): void {
  const url = URL.createObjectURL(new Blob([markdown], { type: 'text/markdown;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `plan-${d.lundiDebut}-au-${d.lundiCible}.md`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
